import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS, HttpInterceptor } from '@angular/common/http';
import { APP_INITIALIZER, Inject, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { ScrollingModule } from '@angular/cdk/scrolling';

import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { IonicModule } from '@ionic/angular';

import { NgReduxRouter, NgReduxRouterModule } from '@angular-redux/router';
import { DevToolsExtension, NgRedux, NgReduxModule } from '@angular-redux/store';
import { MissingTranslationHandler, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';

import {
    AccountTicketsNgrxModule,
    ApplicationFacade,
    CardsNgrxModule,
    CompanyEffects,
    ComponentsService,
    FirebaseModule,
    OpenTripPlannerEffects,
    FavoritesEffects,
    CustomerService,
    SessionService,
    LoginFacade,
    CustomerFacade,
    OpenTripPlannerFacade,
    DeviceNotificationService,
    CustomerAccountsModule,
    CustomerAccountsFacade,
    CoreUiModule
} from '@ridango/components';
import { JourneyPlannerEffects } from '@ridango/components/lib/journey-planner';
import { BannersEffects } from '@ridango/components/lib/banners';
import {
    AppStartupService,
    AuthInterceptorService,
    CacheInterceptor,
    CacheService,
    DeduplicationInterceptor,
    NetworkService,
    NotificationService,
    ZoomService
} from '@ridango/core';
import { LAUNCHDARKLY_CLIENT_ID, FeatureFlagsModule } from '@ridango/feature-flags';
import { UiModule } from '@ridango/ui';
import { WidgetsModule } from '@ridango/widgets';

import { Angulartics2Module } from 'angulartics2';
import { createEpicMiddleware } from 'redux-observable';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { rootReducer, AppState, INITIAL_STATE } from './app.store';
import { MyMissingTranslationHandler } from './handlers/missing-translation.handler';
import { deleteTempStorageState, loadState } from './localStorage';
import { PagesModule } from './pages/pages.module';
import { PendingChangesGuard } from './pending-changes.guard';
import { GotlandActions } from './redux/gotland.actions';
import { NotTranslatedService } from './services/not-translated.service';
import { TranslationsService } from './services/translations.service';
import { SharedModule } from './shared/shared.module';
import { UnauthGuard } from './unauth.guard';
import { AccessGuard } from './access.guard';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { CompanyGuard } from './company.guard';
import { SplashScreenComponent } from './shared/components/splash-screen/splash-screen.component';
import { RedirectGuard } from './redirect.guard';
import { PurchaseGuard } from './purchase.guard';
import Env from '@ridango/core/env';

import { RecaptchaV3Module } from 'ng-recaptcha';
import { LocaleService } from './services/locale.service';
import { CartGuard } from './cart.guard';
import { LayoutModule } from '@angular/cdk/layout';
import { PrintService } from './services/print.service';
import { MessagesService } from './services/messages.service';
import { debounceTime, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ExternalRedirectGuard } from './external-redirect.guard';
import { TicketDurationDescriptionService } from '@ridango/core/lib/services/ticket-duration-description.service';
import { AuthInterceptor, AuthService } from '@ridango/core/lib/services';
import { OfflineTicketDetailsGuard } from './offline-ticket-details.guard';

export function initConfig(config: AppStartupService, env: Env, localeService: LocaleService) {
    return async () => {
        await config.load(env);
        await localeService.getLocaleData().then((locale) => localeService.setLocale(locale));
    };
}

@NgModule({
    declarations: [SplashScreenComponent, AppComponent],
    imports: [
        EffectsModule.forRoot([
            CompanyEffects,
            JourneyPlannerEffects,
            BannersEffects,
            FavoritesEffects,
            OpenTripPlannerEffects
        ]),
        StoreModule.forRoot({}),
        StoreDevtoolsModule.instrument(),
        FeatureFlagsModule.forRoot(),
        BrowserModule,
        HttpClientModule,
        AppRoutingModule,
        NgReduxModule,
        NgReduxRouterModule,
        LayoutModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: TranslationsService,
                deps: [HttpClient, 'ENV']
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: MyMissingTranslationHandler,
                deps: [NotTranslatedService]
            },
            useDefaultLang: false
        }),
        PagesModule,
        SharedModule,
        Angulartics2Module.forRoot(),
        WidgetsModule,
        UiModule,
        ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
        IonicModule.forRoot(),
        FirebaseModule,
        AccountTicketsNgrxModule,
        ScrollingModule,
        RecaptchaV3Module,
        CardsNgrxModule,
        CustomerAccountsModule,
        CoreUiModule
    ],
    providers: [
        NotificationService,
        TranslationsService,
        AppStartupService,
        CustomerAccountsFacade,
        {
            provide: HTTP_INTERCEPTORS,
            useFactory: (
                legacyInterceptor: AuthInterceptorService,
                interceptor: AuthInterceptor,
                env: Env
            ): HttpInterceptor => {
                if (env.WMB?.AUTH) {
                    return interceptor;
                }
                return legacyInterceptor;
            },
            multi: true,
            deps: [AuthInterceptorService, AuthInterceptor, 'ENV']
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CacheInterceptor,
            multi: true,
            deps: [NetworkService, CacheService]
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DeduplicationInterceptor,
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initConfig,
            deps: [AppStartupService, 'ENV', LocaleService, ZoomService, CustomerAccountsFacade],
            multi: true
        },
        {
            provide: 'TRANSLATIONS',
            useValue: {}
        },
        {
            provide: LAUNCHDARKLY_CLIENT_ID,
            useFactory: (env: Env) => {
                return env.LAUNCHDARKLY_CLIENT_ID;
            },
            deps: ['ENV']
        },
        {
            provide: 'googleTagManagerCSPNonce',
            useValue: 'CSP-NONCE'
        },
        GotlandActions,
        NgReduxRouter,
        UnauthGuard,
        PrintService,
        PendingChangesGuard,
        // AuthGuard,
        AccessGuard,
        MessagesService,
        CompanyGuard,
        RedirectGuard,
        PurchaseGuard,
        LocaleService,
        CartGuard,
        ExternalRedirectGuard,
        TicketDurationDescriptionService,
        OfflineTicketDetailsGuard
    ],
    bootstrap: [SplashScreenComponent, AppComponent]
})
export class AppModule {
    private readonly timeoutMs = 5000;

    constructor(
        private customerFacade: CustomerFacade,
        private customerService: CustomerService,
        private loginFacade: LoginFacade,
        public ridangoAuth: SessionService,
        private router: Router,
        private translate: TranslateService,
        ngRedux: NgRedux<AppState>,
        devTools: DevToolsExtension,
        ngReduxRouter: NgReduxRouter,
        componentService: ComponentsService,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private deviceNotificationService: DeviceNotificationService,
        private applicationFacade: ApplicationFacade,
        @Inject('ENV') private env: Env,
        private authService: AuthService
    ) {
        const ios = Capacitor.getPlatform() === 'ios';
        CapacitorApp.addListener('backButton', ({ canGoBack }) => {
            const url = window.location.href;
            if (url.indexOf('/purchase-v2') > 0) {
                //for purchase flow v2 physical back button default behavior is disabled
                return;
            }
            if (url.indexOf('journey-planner/result') > 0) {
                //for purchase flow v2 physical back button default behavior is disabled
                return;
            }
            if (this.applicationFacade.isMenuOpen()) {
                this.applicationFacade.setMobileMenuOpen(false);
                window.history.back();
                return;
            }
            if (canGoBack && url.indexOf('/onboarding') < 0) {
                window.history.back();
            } else {
                CapacitorApp.exitApp();
            }
        });

        CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
            if (ios && isActive) {
                this.openTripPlannerFacade.triggerLocationLocate();
                await this.deviceNotificationService.registerNotifications();
                await this.deviceNotificationService.updateNotifications();
            }
            if (ios && !isActive) {
                this.openTripPlannerFacade.triggerLocateStop();
                this.deviceNotificationService.setPreviousPermission();
            }

            if (isActive && this.env.ENABLE_TERMS_WMB_VERSION && this.authService.getLoginStatus()) {
                this.authService
                    .hasUserAgreedToLatest()
                    .pipe(debounceTime(3000), take(1))
                    .subscribe((hasAgreed) => {
                        if (!hasAgreed) {
                            this.authService
                                .fetchLatestTerms()
                                .pipe(take(1))
                                .subscribe(() => {
                                    this.loginFacade.showTermsModal();
                                });
                        }
                    });
            }

            if (!this.ridangoAuth.getLoginStatus()) {
                return;
            }

            this.customerService
                .fetchCustomerProfile()
                .pipe(take(1))
                .subscribe((customer) => {
                    if (customer.deleted) {
                        this.ridangoAuth.signOut();
                        this.loginFacade.logout();
                        this.customerFacade.clearCustomerProfile();
                        this.router.navigate([this.translate.currentLang, 'login']);
                    }
                });
        });

        if (Capacitor.isPluginAvailable('StatusBar')) {
            StatusBar.setStyle({ style: Style.Light });
        }

        let state = loadState();
        if (!state || window.location.href.indexOf('purchase/result?result') < 0) {
            state = INITIAL_STATE;
        } else {
            deleteTempStorageState();
        }

        const storeEnhancers = devTools.isEnabled() ? [devTools.enhancer()] : [];

        const componentEpics = componentService.getRootEpic();

        const epicMiddleware = createEpicMiddleware();

        ngRedux.configureStore(rootReducer, state, [epicMiddleware], storeEnhancers);

        epicMiddleware.run(componentEpics);

        ngReduxRouter.initialize();
    }
}
